import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  ConfigProvider,
  Dropdown,
  List,
  Pagination,
  Table,
} from 'tea-component';
import { useIntl } from 'umi';
import en_US from 'tea-component/es/i18n/locale/en_US';
import zh_CN from 'tea-component/es/i18n/locale/zh_CN';
import { LANGUAGE_EN } from '@home/_config/config';
import { SortBy } from 'tea-component/lib/table/addons';
import { getName, selectValByCondition } from '@home/utils/common';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import CommonBubble from '../CommonBubble';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import { dynamicString } from '@home/utils/dynamicString';
import './index.scss';


/**
 * 基础表格组件的属性接口定义
 * @interface IBasicTableProps
 * @property {React.CSSProperties} [style] - 表格自定义样式
 * @property {string} [className] - 表格自定义类名
 * @property {any[]} records - 表格数据记录数组
 * @property {string} [recordKey] - 数据记录的唯一标识字段
 * @property {number} recordCount - 数据记录总数
 * @property {number} [minPageSize] - 最小分页大小
 * @property {any} [paginationConfig] - 分页配置
 * @property {any} [expandConfig] - 展开行配置
 * @property {any} [explainInfo] - 说明信息配置
 * @property {boolean} [hasPagination] - 是否显示分页
 * @property {any} [sorterConfig] - 排序配置
 * @property {boolean} [hasFixedColoum] - 是否有固定列
 * @property {string[]} [fixedColumnList] - 固定列列表
 * @property {string} [fixedButtonTextEn] - 固定按钮英文文本
 * @property {string} [fixedButtonTextCn] - 固定按钮中文文本
 * @property {any} [addonsConfig] - 附加功能配置
 * @property {any[]} columns - 表格列配置数组
 * @property {boolean} [stickyHeader] - 是否启用表头固定
 */
export interface IBasicTableProps {
  style?: React.CSSProperties;
  className?: string;
  records: any[];
  recordKey?: string;
  recordCount: number;
  minPageSize?: number;
  paginationConfig?: any;
  expandConfig?: any;
  explainInfo?: any;
  hasPagination?: boolean;
  sorterConfig?: any;
  hasFixedColoum?: boolean;
  fixedColumnList?: string[];
  fixedButtonTextEn?: string;
  fixedButtonTextCn?: string;
  addonsConfig?: any;
  columns: any[];
  stickyHeader?: boolean;
  [key: string]: any;
}

const { sortable, expandable } = Table.addons;

const BasicTable = (props: IBasicTableProps) => {
  const {
    style = {},
    className,
    columns,
    records,
    recordKey = 'key',
    recordCount,
    minPageSize = 5,
    paginationConfig = {},
    expandConfig: propsExpandConfig,
    explainInfo,
    hasPagination = true,
    sorterConfig,
    hasFixedColoum,
    fixedColumnList,
    fixedButtonTextEn = 'Freeze',
    fixedButtonTextCn = '冻结',
    addonsConfig,
    stickyHeader = true,
    ...restProps
  } = props;

  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [checkFixedCols, setCheckFixedCols] = useState<string[]>([]);

  useEffect(() => {
    if (fixedColumnList) {
      setCheckFixedCols(fixedColumnList);
    }
  }, [fixedColumnList]);

  // 首列冻结选项
  const fixedDropdownDom = useMemo(() => {
    if (!hasFixedColoum) return null;
    return (
      <Dropdown
        clickClose={false}
        button={getName(isEn, fixedButtonTextEn, fixedButtonTextCn)}
        appearance="link"
        boxClassName="dbh-basic-table-fixed-header-dropdown"
      >
        <List type="option">
          <Checkbox.Group
            value={checkFixedCols}
            onChange={(value) => setCheckFixedCols(value)}
          >
            {columns?.map((item: any) => {
              const { nameEn, nameCn, metricKey } = item || {};
              const header = getName(isEn, nameEn, nameCn);
              return (
                <List.Item key={metricKey}>
                  <Checkbox name={metricKey}>{header}</Checkbox>
                </List.Item>
              );
            })}
          </Checkbox.Group>
        </List>
      </Dropdown>
    );
  }, [
    checkFixedCols,
    columns,
    fixedButtonTextCn,
    fixedButtonTextEn,
    hasFixedColoum,
    isEn,
  ]);

  // 处理column配置
  const newColumns = useMemo(() => {
    const cols = columns || [];
    return cols?.map((item: any, index: number) => {
      const {
        metricKey,
        reqMetricKey,
        widthEn,
        widthCn,
        nameEn,
        nameCn,
        rainbowKey,
        valueFormat,
        sort,
        render,
        headerRender,
        tooltipEn,
        tooltipCn,
        align,
        fixed,
      } = item;

      // 获取colunmn注释，explainInfo为七彩石配置数据
      const explainText = getExplainVal(
        explainInfo,
        rainbowKey,
        locale,
        tooltipEn,
        tooltipCn,
      );
      const header = getName(isEn, nameEn, nameCn);

      let colFixed = fixed;
      // 匹配冻结列
      if (hasFixedColoum && checkFixedCols.includes(metricKey)) {
        colFixed = 'left';
      }

      return {
        key: reqMetricKey || metricKey,
        width: selectValByCondition(isEn, widthEn, widthCn),
        header: () => {
          // 首列可选冻结列
          let fixedDropdown;
          if (hasFixedColoum && index === 0) {
            fixedDropdown = fixedDropdownDom;
          }
          // 自定义表头内容
          if (typeof headerRender === 'function') {
            return headerRender(header, explainText, item);
          }
          const headerDom = <span className="tea-text-overflow">{header}</span>;
          return (
            <CommonBubble
              overlayClassName="dbh-basic-table-fixed-header-bubble"
              content={
                explainText && (
                  <div dangerouslySetInnerHTML={{ __html: explainText }} />
                )
              }
            >
              {fixedDropdown ? (
                <div className="dbh-basic-table-fixed-header">
                  {headerDom}
                  {fixedDropdown}
                </div>
              ) : (
                headerDom
              )}
            </CommonBubble>
          );
        },
        render: (record: any) => {
          if (typeof render === 'function') {
            return render(record, item);
          }
          if (valueFormat) {
            return dataFormat({
              value: dynamicString(metricKey, record),
              formatRule: valueFormat,
            });
          }
          return dynamicString(metricKey, record) || '-';
        },
        sort,
        align,
        fixed: colFixed,
      };
    });
  }, [
    checkFixedCols,
    columns,
    explainInfo,
    fixedDropdownDom,
    hasFixedColoum,
    isEn,
    locale,
  ]);

  // 筛选排序字段
  const sortableConfig = useMemo(() => {
    const sortColumns =
      newColumns?.filter(({ header, sort }: any) => !!header && sort) || [];
    const sortKeyList = sortColumns?.map((item: any) => ({
      key: item.key,
      prefer: 'desc',
    }));
    const { onSortChange, sorts, ...otherOptions } = sorterConfig || {};
    return {
      columns: sortKeyList,
      value: sorts,
      onChange: (value: SortBy[], context: any) => {
        const sortList = value.length ? [context.sort] : []; // 限制为单列排序
        onSortChange?.(sortList);
      },
      ...otherOptions,
    };
  }, [newColumns, sorterConfig]);

  // 展开配置
  const expandConfig = useMemo(() => {
    const { expandedKeys, onExpandedChange, ...otherOptions } =
      propsExpandConfig || {};
    return {
      expandedKeys,
      expand: (record: any) => record?.children,
      onExpandedKeysChange: (keys: string[], { event }: any) => {
        event.stopPropagation();
        onExpandedChange?.(keys, { event });
      },
      shouldRecordExpandable: (record: any) => record?.children,
      ...otherOptions,
    };
  }, [propsExpandConfig]);

  return (
    <div
      className={classNames('dbh-basic-table-container', { 'dbh-basic-table-sticky-header': stickyHeader }, className)}
      style={style}
    >
      <Table
        {...restProps}
        records={records}
        recordKey={recordKey}
        columns={newColumns}
        addons={[
          sortable(sortableConfig),
          propsExpandConfig ? expandable(expandConfig) : {},
          ...(addonsConfig || []),
        ]}
      />
      {hasPagination && recordCount > minPageSize && (
        <ConfigProvider locale={locale === LANGUAGE_EN ? en_US : zh_CN}>
          <Pagination
            recordCount={recordCount}
            stateText={`${recordCount} ${formatMessage({
              id: 'common_records',
            })}`}
            pageSizeOptions={[5, 10, 20, 30, 40, 50]}
            {...paginationConfig}
          />
        </ConfigProvider>
      )}
    </div>
  );
};

export default BasicTable;
